import React from 'react';
import { Bell, ChefHat, Wine, Send, Plus, Users } from 'lucide-react';
import { formatPrice } from '../../utils/translations';
import { BEVERAGE_CATEGORIES, getElapsedTime } from './posConstants';

export const DashboardView = ({
  pendingOrders, tableSessions, transferOrder, startNewOrder,
  setSelectedTable, setViewMode, t
}) => {
  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="dashboard-view">
      {/* Incoming Orders Section */}
      {pendingOrders.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-black mb-4 flex items-center gap-2">
            <Bell className="text-amber-400" size={20} />
            {t.incomingOrders} ({pendingOrders.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingOrders.map(order => {
              const foodItems = order.items?.filter(item => !BEVERAGE_CATEGORIES.includes(item.category)) || [];
              const drinkItems = order.items?.filter(item => BEVERAGE_CATEGORIES.includes(item.category)) || [];
              return (
                <div key={order.id} className="bg-slate-800 rounded-2xl p-4 border-2 border-amber-500/50 animate-pulse" data-testid={`incoming-order-${order.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-black">#{order.order_number}</h3>
                      <p className="text-sm text-slate-400">{t.table} {order.table}</p>
                    </div>
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-bold">
                      {getElapsedTime(order.created_at)}
                    </span>
                  </div>
                  <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-300">{item.quantity}x {item.name?.en || item.name}</span>
                        <span className={BEVERAGE_CATEGORIES.includes(item.category) ? 'text-purple-400' : 'text-amber-400'}>
                          
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mb-3">
                    {foodItems.length > 0 && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-bold">
                        <ChefHat size={12} />
                        {foodItems.length} {t.foodItems}
                      </span>
                    )}
                    {drinkItems.length > 0 && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-xs font-bold">
                        <Wine size={12} />
                        {drinkItems.length} {t.drinkItems}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => transferOrder(order)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-400"
                    data-testid={`transfer-order-${order.id}`}
                  >
                    <Send size={18} />
                    {t.sendAll}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Table Selection */}
      <div className="mb-8">
        <h2 className="text-lg font-black mb-4">{t.quickTables}</h2>
        <div className="flex flex-wrap gap-3">
          {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(num => {
            const hasSession = tableSessions.some(s => s.table === num);
            return (
              <button
                key={num}
                onClick={() => {
                  if (hasSession) {
                    setSelectedTable(tableSessions.find(s => s.table === num));
                    setViewMode('orderDetail');
                  } else {
                    startNewOrder(num);
                  }
                }}
                className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center transition-all ${
                  hasSession
                    ? 'bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/30'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white border border-dashed border-slate-700'
                }`}
                data-testid={`quick-table-${num}`}
              >
                <span className="text-lg font-black">{num}</span>
                <span className="text-[10px] font-bold uppercase">{hasSession ? t.active : t.empty}</span>
              </button>
            );
          })}
          <button
            onClick={() => startNewOrder()}
            className="w-16 h-16 rounded-xl bg-slate-800 text-slate-400 flex flex-col items-center justify-center hover:bg-slate-700 hover:text-white"
            data-testid="quick-table-custom"
          >
            <Plus size={20} />
            <span className="text-[10px] font-bold uppercase">{t.customTable}</span>
          </button>
        </div>
      </div>

      {/* Active Tables */}
      <div>
        <h2 className="text-lg font-black mb-4 flex items-center gap-2">
          <Users size={20} className="text-emerald-400" />
          {t.activeTables} ({tableSessions.length})
        </h2>
        {tableSessions.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl p-12 text-center border border-slate-700">
            <Users size={48} className="mx-auto mb-4 text-slate-600" />
            <h3 className="text-lg font-bold text-slate-400 mb-2">{t.noTables}</h3>
            <p className="text-slate-500 text-sm">{t.noTablesDesc}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {tableSessions.map(session => {
              const totalAmount = (session.orders || []).reduce((sum, o) =>
                sum + (o.items || []).reduce((s, i) => s + (i.price * i.quantity), 0), 0
              );
              return (
                <div
                  key={session.id}
                  onClick={() => { setSelectedTable(session); setViewMode('orderDetail'); }}
                  className="bg-slate-800 rounded-2xl p-5 cursor-pointer hover:bg-slate-750 border border-slate-700 hover:border-amber-500/50 transition-all"
                  data-testid={`active-table-${session.table}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-black">{t.table} {session.table}</h3>
                      <p className="text-xs text-slate-500">{getElapsedTime(session.created_at)} {t.minutesAgo}</p>
                    </div>
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-bold">
                      {(session.orders || []).length} {t.orders}
                    </span>
                  </div>
                  <div className="flex justify-between items-end">
                    <span className="text-slate-500 text-sm">{t.total}</span>
                    <span className="text-xl font-black text-amber-400">{formatPrice(totalAmount)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
